import { createSelector } from 'reselect';
import { fromJS } from 'immutable';

import moment from 'moment';
import _, { isObject } from 'lodash';
import UserPermissions from 'constants/userPermissions';
import { getAssessmentTerms } from 'utils/pluralizeAndFormat';
import { DEFAULT_ASSESSMENT_TERMS } from 'constants/appConstants';
import { selectUserPermissions as selectAccountPermissions } from '../Home/selectors';
import { selectWidgetsDomain } from '../Widgets/selectors';
import { APP_TYPES } from './TeamApp/constants';
import { getNullPermission } from './TeamApp/permissions';

/**
 * Direct selector to the team state domain
 */

const getTeams = (state) => state.getIn(['teams', 'teams']).toJS();

const selectTeamDomain = () => (state) => state.get('team');

const selectTeam = () =>
  createSelector(selectTeamDomain(), (substate) => {
    const team = substate.get('team').toJS();
    if (!team) {
      return {};
    }
    return {
      ...team,
      create_date: moment(team.create_date),
    };
  });

export const selectTeamSolutions = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('solutions').toJS()
  );

export const selectActiveSolutions = () =>
  createSelector(selectTeamSolutions(), (subState) =>
    subState.filter((solution) => !solution.deleted_at)
  );

export const selectDefaultSolution = () =>
  createSelector(selectActiveSolutions(), (solutions) =>
    solutions.length > 0 ? solutions[0] : {}
  );

export const selectPublishedTeamSolutionVersions = () =>
  createSelector(selectActiveSolutions(), (subState) =>
    subState.filter((solution) => solution.solution_version.published_at)
  );

export const selectApps = () =>
  createSelector(selectTeamSolutions(), (subState) =>
    subState.reduce((acc, curr) => {
      const apps = _.get(curr, 'solution_version.modules', []);
      return [...acc, ...apps];
    }, [])
  );

export const selectPublishedApps = () =>
  createSelector(selectPublishedTeamSolutionVersions(), (subState) =>
    subState.reduce((acc, curr) => {
      const apps = _.get(curr, 'solution_version.modules', []);
      return [...acc, ...apps];
    }, [])
  );

const sortFunc = (a, b) => {
  if (a.module_name < b.module_name) {
    return -1;
  }
  if (a.module_name > b.module_name) {
    return 1;
  }
  return 0;
};

const sortApps = (modules) => {
  const apps = modules.reduce(
    (acc, curr) => {
      if (curr.app_type === 'flow') {
        return { ...acc, flow: [...acc.flow, curr] };
      }
      return { ...acc, data: [...acc.data, curr] };
    },
    {
      flow: [],
      data: [],
    }
  );

  apps.flow.sort(sortFunc);
  apps.data.sort(sortFunc);

  return [...apps.flow, ...apps.data];
};

export const selectActiveApps = () =>
  createSelector(selectActiveSolutions(), (solutions) =>
    sortApps(
      solutions.reduce((acc, curr) => {
        const modules = _.get(curr, 'solution_version.modules', []);

        return [...acc, ...modules];
      }, [])
    )
  );

const selectIsTogglingActivate = () =>
  createSelector(selectTeamDomain(), (subState) =>
    subState.get('togglingApps')
  );

export const selectIsCreatingAssessment = () =>
  createSelector(
    selectTeamDomain(),
    (subState) =>
      subState?.get('isCreatingAssessment') || { status: false, appId: '' }
  );

export const selectTeamID = () =>
  createSelector(selectTeam(), (substate) => substate.team_id);

const selectTeamAssessmentTerms = (appID) =>
  createSelector(selectTeamSolutions(), (solutions) => {
    const apps = solutions.reduce((acc, cur) => {
      const solutionApps = _.get(cur, 'solution_version.modules', []);
      return [...acc, ...solutionApps];
    }, []);

    // eslint-disable-next-line eqeqeq -- module_id may be string or number; preserved behavior
    const app = apps.find((a) => a.module_id == appID);

    if (!app) {
      console.warn('Could not find app with id: ', appID);
    }

    const terms = getAssessmentTerms(
      _.get(app, 'terms_assessment', DEFAULT_ASSESSMENT_TERMS)
    );

    return terms;
  });

// Not removing in case we still need to use state for this
export const buildTeamPermissions = () => {
  const { teamAdmin, teamMember } = UserPermissions;

  return [teamAdmin, teamMember];
};

export const selectAvailableTeamPermissions = () =>
  createSelector(selectTeamDomain(), () => buildTeamPermissions());

const selectWorkflowGroups = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('workflowGroups').toJS()
  );

const selectError = () =>
  createSelector(selectTeamDomain(), (substate) => substate.get('error'));

const selectLoading = () =>
  createSelector(selectTeamDomain(), (substate) => substate.get('loading'));

const selectDeletingAssessment = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('deletingAssessment')
  );

const selectTeamWidgets = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('widgets').toJS()
  );

const selectLoadingTeamWidgets = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('loadingWidgets')
  );

const selectLoadingApps = () =>
  createSelector(selectTeamDomain(), (substate) => substate.get('loadingApps'));

const selectSelectedTeamWidgetID = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('selectedWidget')
  );

const selectSelectedTeamWidget = () =>
  createSelector(
    selectTeamWidgets(),
    selectSelectedTeamWidgetID(),
    (widgets, selectedID) => widgets.find((widget) => widget.id === selectedID)
  );

const selectImportedAssessments = () =>
  createSelector(selectTeamDomain(), (substate) => {
    if (substate) {
      const importedAssessments = substate.get('importedAssessments').toJS();
      importedAssessments.forEach((assessment) => {
        assessment.createdAt = moment(assessment.createdAt);
      });
      return importedAssessments;
    }
    return [];
  });

const selectExecutedWidgetId = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('selectedWidget')
  );

export const selectExecutedWidget = () =>
  createSelector(
    selectExecutedWidgetId(),
    selectWidgetsDomain(),
    (id, substate) => fromJS(substate.getIn([id, 'widget']))
  );

export const selectExecutedWidgetDefinition = () =>
  createSelector(
    selectExecutedWidgetId(),
    selectWidgetsDomain(),
    (id, substate) => fromJS(substate.getIn([id, 'definition']))
  );

export const selectWidgetFilters = () =>
  createSelector(selectTeamDomain(), (substate) => substate.get('filters'));

export const selectLoadingWidgets = () =>
  createSelector(
    selectTeamDomain(),
    (substate) =>
      substate.get('loadingExecutedWidget') || substate.get('loadingWidgets')
  );

export const selectAppType = (appID) =>
  createSelector(selectTeamSolutions(), (solutions) => {
    const apps = solutions.reduce((acc, cur) => {
      const solutionApps = _.get(cur, 'solution_version.modules', []);
      return [...acc, ...solutionApps];
    }, []);

    // eslint-disable-next-line eqeqeq -- module_id may be string or number; preserved behavior
    const app = apps.find((a) => a.module_id == appID);

    if (!app) {
      console.warn('Could not find app with id: ', appID);
    }

    return _.get(app, 'app_type', APP_TYPES.FLOW);
  });

export const selectIsAppPublic = (appID) =>
  createSelector(selectTeamSolutions(), (solutions) => {
    const apps = solutions.reduce((acc, cur) => {
      const solutionApps = _.get(cur, 'solution_version.modules', []);
      return [...acc, ...solutionApps];
    }, []);

    // eslint-disable-next-line eqeqeq -- module_id may be string or number; preserved behavior
    const app = apps.find((a) => a.module_id == appID);

    if (!app) {
      console.warn('Could not find app with id: ', appID);
    }

    return !_.get(app, 'is_private', true);
  });

export const selectErrorExecutingWidget = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('errorExecutingWidget')
  );

createSelector(selectTeamDomain(), (substate) =>
  substate.get('teamAssessments')
);

createSelector(selectTeamDomain(), (substate) => ({
  limit: substate.get('limit'),
  offset: substate.get('offset'),
  search: substate.get('search'),
}));

export const selectTeamRole = () =>
  createSelector(selectTeamDomain(), (substate) => substate.get('teamRole'));

export const selectLoadingUserTeamPermissions = () =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.get('loadingUserPermissions')
  );

export const selectTeamPermissions = () =>
  createSelector(
    selectAccountPermissions(),
    selectTeamRole(),
    (accountPermissions, teamRole) => {
      const canManageTeam = accountPermissions.includes(
        UserPermissions.manageTeams.code
      );
      const isTeamAdmin = teamRole === UserPermissions.teamAdmin.code;
      const canViewMembers = canManageTeam || isTeamAdmin;
      const canManageTeamApp =
        isTeamAdmin ||
        accountPermissions.includes(UserPermissions.assignModulesToTeams.code);
      const canPublishVersion = accountPermissions.includes(
        UserPermissions.publishModules.code
      );
      const isTeamMember = teamRole === UserPermissions.teamMember.code;

      return {
        canManageTeamApp,
        canManageTeam,
        canViewMembers,
        isTeamAdmin,
        isTeamMember,
        canPublishVersion,
      };
    }
  );

export const selectAppByID = (id) =>
  createSelector(
    selectApps(),
    (apps) => _.find(apps, (app) => app.module_id === _.toInteger(id)) || {}
  );

export const selectApp = () =>
  createSelector(selectTeamDomain(), (team) => team.get('app').toJS() || {});

export const selectIsGlobalTeam = () =>
  createSelector(selectTeam(), (substate) => substate.global);

export const selectSolutionPermissions = () =>
  createSelector(selectTeamDomain(), (substate) => substate.get('permissions'));

export const selectAppPermission = (appID) =>
  createSelector(selectTeamDomain(), (substate) =>
    substate.getIn(['appPermissions', `${appID}`], fromJS(getNullPermission()))
  );

export default selectTeam;
export {
  getTeams,
  selectTeam,
  selectTeamWidgets,
  selectLoadingTeamWidgets,
  selectLoadingApps,
  selectDeletingAssessment,
  selectSelectedTeamWidget,
  selectSelectedTeamWidgetID,
  selectError,
  selectLoading,
  selectWorkflowGroups,
  selectImportedAssessments,
  selectTeamAssessmentTerms,
  selectIsTogglingActivate,
};
